import { getEnv } from "./env";

type ApiConfig = {
  apiUrl: string;
  nonApiUrl: string;
  isProduction: boolean;
};

type ApiResponse = Record<string, any>;

export class FeegowLabApi {
  /**
   * Get the Feegow Lab API URL for the current environment
   */
  static getFeegowLabApiUrl(): {
    url: string;
  } {
    if (window.location.hostname.includes("localhost")) {
      return { url: "http://localhost:8001" };
    }
    if (window.location.hostname.includes("staging")) {
      return {
        url: getEnv(
          "VUE_APP_LABS_API_URL",
          "https://labs.staging.feegow.com"
        ) as string,
      };
    }
    if (
      ["dev", "milestone", "/qa/"].some(function (p) {
        return window.location.hostname.includes(p);
      })
    ) {
      return { url: "https://labs.dev.feegow.com" };
    }
    return {
      url: getEnv("VUE_APP_LABS_API_URL", "https://labs.feegow.com") as string,
    };
  }

  static getFeegowLabApiConfig(): ApiConfig {
    const { url: baseUrl } = FeegowLabApi.getFeegowLabApiUrl();
    const isProduction = baseUrl === "https://labs.feegow.com";

    return {
      apiUrl: `${baseUrl}/api/labs-integration`,
      nonApiUrl: `${baseUrl}/labs-integration`,
      isProduction,
    };
  }

  static getToken(): string {
    const token = sessionStorage.getItem("tk") ?? localStorage.getItem("tk");
    if (!token) throw new Error("Token not found in local storage");
    return token;
  }

  static buildUrl(
    route: string,
    params?: Record<string, string>,
    useNonApiUrl = false
  ): string {
    const { apiUrl, nonApiUrl } = this.getFeegowLabApiConfig();
    const token = this.getToken();

    const queryParams = new URLSearchParams({ ...params, tk: token });
    const baseUrl = useNonApiUrl ? nonApiUrl : apiUrl;
    return `${baseUrl}${route}?${queryParams.toString()}`;
  }

  static async fetch<T = ApiResponse>(
    method: string,
    route: string,
    params?: Record<string, string>,
    returnType: "json" | "text" = "json",
    useNonApiUrl = false
  ): Promise<T> {
    const fullUrl = this.buildUrl(route, params, useNonApiUrl);

    const response = await fetch(fullUrl, { method });
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

    return returnType === "text"
      ? ((await response.text()) as unknown as T)
      : ((await response.json()) as T);
  }

  static async getDetalhesSolicitacao(
    microservico: string,
    id: number,
    config?: { labWatson?: boolean }
  ): Promise<{ style: string; html: string }> {
    const params: Record<string, string> = {
      microservico,
      id: id.toString(),
    };

    // Adiciona config[labWatson]=true SOMENTE se config existir e labWatson for true
    if (config && config.labWatson === true) {
      params["config[labWatson]"] = "true";
    }

    const url = this.buildUrl("/modal-detalhes-solicitacao", params, true);
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

    const responseText = await response.text();
    const styleMatch = responseText.match(/<style[^>]*>(.*?)<\/style>/s);
    const htmlMatch = responseText.match(/<body[^>]*>(.*?)<\/body>/s);

    return {
      style: styleMatch ? styleMatch[1] : "",
      html: htmlMatch ? htmlMatch[1] : responseText,
    };
  }

  static async getSamples(
    dateStart: string,
    dateEnd: string,
    patientId: string | null = null,
    priority: string | null = null
  ): Promise<ApiResponse> {
    const params: Record<string, string> = { dateStart, dateEnd };

    if (priority && priority !== "Todos") {
      params.priority = priority;
    }
    if (patientId) {
      params.patientId = patientId;
    }

    return await this.fetch<ApiResponse>("GET", "/retorna-amostras", params);
  }

  static async changeSampleStatus(
    sampleId: string,
    statusId: string,
    tipo: string
  ): Promise<ApiResponse> {
    return await this.fetch<ApiResponse>("POST", "/altera-amostras-status", {
      id: sampleId,
      statusId,
      tipo,
    });
  }

  static async imprimirEtiquetas(
    sampleId: string,
    endpoint: string,
    config?: { labWatson?: boolean }
  ): Promise<{ html: string }> {
    const params: Record<string, string> = {
      origem: "solicitacao",
      id: sampleId,
    };

    // Adiciona config[labWatson]=true SOMENTE se config existir e labWatson for true
    if (config && config.labWatson === true) {
      params["config[labWatson]"] = "true";
    }

    const url = this.buildUrl(`/${endpoint}`, params, true);

    const response = await fetch(url);
    if (!response.ok)
      throw new Error("Falha ao carregar o conteúdo da etiqueta.");
    return { html: await response.text() };
  }

  static async imprimirComprovanteColeta(
    sampleId: string,
    config?: { labWatson?: boolean }
  ): Promise<{ html: string }> {
    const params: Record<string, string> = {
      origem: "solicitacao",
      id: sampleId,
    };

    // Adiciona config[labWatson]=true SOMENTE se config existir e labWatson for true
    if (config && config.labWatson === true) {
      params["config[labWatson]"] = "true";
    }

    const url = this.buildUrl("/imprimir-protocolo-solicitacao", params, true);

    return {
      html: url,
    };
  }

  static async fetchListagemExamesView(): Promise<ApiResponse> {
    const url = "/get-list-exams-license";
    return await this.fetch<ApiResponse>("GET", url, { labid: "5" });
  }

  static async fetchExamDetails(id: string): Promise<ApiResponse> {
    return await this.fetch("GET", "/get-exam", { id });
  }

  static async getSamplesByLab(params: {
    labId: string;
  }): Promise<ApiResponse> {
    return await this.fetch("GET", "/get-samples-license-by-labid", {
      labId: params.labId,
    });
  }

  static async getProcedures(params?: {
    labId?: string;
  }): Promise<ApiResponse> {
    return await this.fetch("GET", "/get-procedures", params);
  }

  static async insertExamLicense(
    examData: Record<string, any>
  ): Promise<ApiResponse> {
    return await this.fetch<ApiResponse>(
      "POST",
      "/insert-exams-license",
      examData
    );
  }

  static async updateExamLicense(
    examData: Record<string, any>
  ): Promise<ApiResponse> {
    return await this.fetch<ApiResponse>(
      "POST",
      "/update-exams-license",
      examData
    );
  }

  static async fetchSampleDetails(id: string): Promise<ApiResponse> {
    return await this.fetch("GET", "/get-sample", { id });
  }

  static async fetchListagemAmotrasView(): Promise<ApiResponse> {
    const url = "/get-list-samples-license";
    return await this.fetch<ApiResponse>("GET", url, { labid: "5" });
  }

  static async insertSampleLicense(
    sampleData: Record<string, any>
  ): Promise<ApiResponse> {
    return await this.fetch<ApiResponse>(
      "POST",
      "/insert-sample-license",
      sampleData
    );
  }

  static async updateSampleLicense(
    sampleData: Record<string, any>
  ): Promise<ApiResponse> {
    return await this.fetch<ApiResponse>(
      "POST",
      "/update-sample-license",
      sampleData
    );
  }
}
